<script setup>
import {Device, Generic} from '@/paks/mgr-models'
import {onBeforeMount, reactive, ref} from 'vue'
import {Feedback, Progress, State, can, clone, getSchemaModel, go, titlecase} from '@/paks/vu-app'
import Json5 from 'json5'

const TypeField = '_type'
let Forever = new Date(2100, 1, 1)

const props = defineProps({
    item: Object,
    model: String,
    panel: Object,
    title: String,
})
const emit = defineEmits(['input', 'update:modelValue'])

const page = reactive({
    canDelete: false,
    canEdit: true,
    fields: [],
    hasCloud: State.config.features.cloud?.enable,
    item: null,
    releasing: false,
    removing: false,
    rules: [],
    roles: [],
    panel: null,
    saving: false,
    title: null,
    values: {},
})

const confirm = ref(null)

onBeforeMount(async () => {
    page.item = clone(props.item)
    page.schema = State.app.schema
    page.fields = props?.panel?.fields?.filter((f) => can(f.role)) || []
    page.canRelease = props.model == 'Device' && page.hasCloud

    page.canDelete = can('admin')
    page.canEdit = true
    if (!page.canEdit) {
        page.canDelete = false
    }
    if (!page.item.id) {
        page.item.created = new Date()
        page.item.updated = new Date()
    }
    if (page.fields.length == 0) {
        let model = getSchemaModel(props.model)

        if (props.panel?.properties) {
            page.fields = []
            let fields = props.panel.properties[props.model]?.fields || []
            for (let field of fields) {
                if (typeof field == 'string') {
                    page.fields.push({name: field, type: model[field].type || 'string'})
                } else {
                    page.fields.push(field)
                }
            }
        }
        if (page.fields.length == 0) {
            if (model) {
                let fields = Object.keys(model).map((name) => {
                    return {name, type: model[name].type}
                })
                page.fields = fields.filter((f) => f.name != 'pk' && f.name != 'sk' && f.name != '_source')
            }
        }
    }
    for (let field of page.fields) {
        field.value = page.item[field.name]
        let sfield = page.schema.models[props.model] || {}
        if (sfield[field.name]?.hidden) {
            field.disabled = true
        } else if (page.item.id && sfield[field.name]?.readonly) {
            field.disabled = true
        } else if (field.name == 'created') {
            field.disabled = true
        } else if (props.model == 'Store' && field.name == 'key' && page.item.id) {
            field.disabled = true
        } else if (field.name == 'expires') {
            page.item.expires = page.item.expires || Forever
            field.format = (v) => (!v || v.getTime() == 0 ? 'never' : v)
        }
        if (page.item.id == null) {
            if (field.name == 'deviceId' && page.item.deviceId == null) {
                page.item.deviceId = State.app.context.deviceId
            } else if (field.name == 'accountId' && page.item.accountId == null) {
                page.item.accountId = State.auth.accountId
            }
        }
    }
    let type = page.item[TypeField] || props.model
    page.title = props.title || `${type ? 'Modify' : 'Create'} ${props.model} Item`
})

async function save() {
    let item = page.item
    for (let field of page.fields) {
        if (field.type == 'object' && typeof item[field.name] == 'string') {
            item[field.name] = Json5.parse(item[field.name])
        }
    }
    item.deviceId = State.app.context.deviceId
    if (item[TypeField]) {
        await Generic.update(item[TypeField], item)
    } else {
        item[TypeField] = item[TypeField] || props.model
        await Generic.create(item[TypeField], item)
    }
    emit('input', item)
}

async function remove() {
    if (await confirm.value.ask(`Do you want to remove this item? `)) {
        try {
            let item = page.item
            page.removing = true
            await Generic.remove(item._type, item)
            Feedback.info('Item Removed')
            emit('input')
        } catch (err) {
            Feedback.error(err.message)
        } finally {
            page.removing = true
        }
    }
}

async function release() {
    if (await confirm.value.ask(`Do you want to release this device? `)) {
        try {
            let item = page.item
            page.releasing = true
            Progress.start('dialog', `Releasing ${item.id}`)
            await Device.remove({id: item.id})
            if (State.app.context.deviceId == item.id) {
                State.app.setContext('deviceId', null)
            }
            Feedback.info('Device Released')
            emit('input')
            go({name: 'devices'})
        } catch (err) {
            Feedback.error(err.message)
        } finally {
            Progress.stop()
            page.releasing = true
        }
    }
}

function getFieldType(field) {
    let type = field.type
    switch (type) {
        case 'date':
            return 'date'
        case 'number':
            return 'text'
        case 'string':
            return 'text'
        case 'select':
            return 'select'
        case 'object':
            return 'textarea'
        default:
            return field.type || 'text'
    }
}
</script>

<template>
    <vu-form :data="page" :title="page.title" :save="save" :options="{enter: false}">
        <vu-input
            v-for="field of page.fields.filter((f) => can(f.role))"
            v-model="page.item[field.name]"
            :cols="field.width"
            :disabled="field.disabled || !page.canEdit"
            :items="field.items"
            :key="field.name"
            :label="field.label || titlecase(field.name)"
            :name="field.name"
            :rules="page.rules[field.rules]"
            :type="getFieldType(field)" />

        <div class="actions">
            <v-btn v-if="page.canEdit" color="accent" type="submit" :loading="page.saving">Save</v-btn>
            <v-btn size="small" color="none" @click="emit('input')">Cancel</v-btn>
            <v-btn
                v-if="page.item[TypeField] && page.canDelete && !page.canRelease"
                dark
                size="small"
                color="error"
                :loading="page.removing"
                @click="remove">
                Delete
            </v-btn>
            <v-btn v-if="page.canRelease" size="small" dark color="teal" :loading="page.releasing" @click="release">
                Release Device
            </v-btn>
        </div>
        <vu-confirm ref="confirm" />
    </vu-form>
</template>
